import {
  Control,
  FieldValues,
  Path,
  useController,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SelectInput } from '../select-input'
import { ListItemBorderBox } from '../../components/list-item-border-box'
import { DialogInput } from '../dialog-input'
import { Stack } from '@mui/material'

type Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> = {
  control: Control<TFieldValues>
  name: TName
  label: string
  helperText?: string
  dialogInputProps: Omit<
    React.ComponentProps<typeof DialogInput>,
    'name' | 'control' | 'onChange'
  >
}

export const ConditionalDialogInput = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
) => {
  const {
    control: parentControl,
    name,
    label,
    helperText,
    dialogInputProps,
  } = props
  const {
    field: { value: parentValue, onChange },
  } = useController<TFieldValues, TName>({ name, control: parentControl })
  const { t } = useTranslation(['filter'])
  const parentInventoryFeatures = parentValue

  const { control, getValues } = useForm({
    defaultValues: {
      allowAll: !parentInventoryFeatures,
      selectedItems: parentInventoryFeatures || [],
    },
  })

  const limitInventoryFeaturesOptions = [
    {
      name: t('filter:conditional_dialog_input.allow_all'),
      value: true,
    },
    {
      name: t('filter:conditional_dialog_input.limit'),
      value: false,
    },
  ]

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 2,
      }}
    >
      <SelectInput
        name="allowAll"
        control={control}
        label={label}
        options={limitInventoryFeaturesOptions}
        onChange={(event) => {
          if (event.target.value) return onChange(null)
          else return onChange(getValues('selectedItems'))
        }}
        helperText={helperText}
      />
      {!getValues('allowAll') && (
        <ListItemBorderBox
          sx={{
            p: 0,
          }}
        >
          <DialogInput
            name="selectedItems"
            control={control}
            {...dialogInputProps}
            onChange={(values) => {
              onChange(values)
            }}
            itemLabel={t('filter:conditional_dialog_input.selection')}
          />
        </ListItemBorderBox>
      )}
    </Stack>
  )
}
